import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError, EMPTY, Observable } from 'rxjs';
import { catchError, expand, map, reduce, tap } from 'rxjs/operators';

import { Generator } from '@digital-platform/generators/domain';

@Injectable({
  providedIn: 'root',
})
export class GeneratorsService {
  constructor(private httpClient: HttpClient) {}

  public create(newGenerator: Generator): Observable<Generator> {
    const url = '/api/generators';
    return this.httpClient
      .post<Generator>(url, newGenerator)
      .pipe(catchError(this.handleError));
  }

  public find(id: string): Observable<Generator> {
    const url = `/api/generators/${id}`;
    return this.httpClient
      .get<Generator>(url)
      .pipe(catchError(this.handleError));
  }

  public update(id: string, generator: Generator): Observable<Generator> {
    const url = `/api/generators/${id}`;
    return this.httpClient
      .put<Generator>(url, generator)
      .pipe(catchError(this.handleError));
  }

  public deleteGenerator(id: string): Observable<Generator> {
    const url = `/api/generators/${id}`;
    return this.httpClient
      .delete<Generator>(url)
      .pipe(catchError(this.handleError));
  }

  public findAll(
    customerName,
    loadingId: string,
    callback
  ): Observable<Generator[]> {
    const url = '/api/generators';
    return this.getResponse(
      url,
      { pageSize: '250', customerName },
      { 'X-Loading-Id': loadingId }
    ).pipe(
      expand((response) => {
        if (response.body.length === 0) {
          // we got an empty response, nothing to do here
          return EMPTY;
        }

        const ExclusiveStartKey = response.headers.get('X-Rsc-Page');

        if (response.body.length === 0) {
          // we got back and emppy page, we almost certainly have them all
          return EMPTY;
        }

        if (ExclusiveStartKey) {
          // we got back results, there may be more, but since we don't have a total,
          // we have to blindly fetch the next page until we run out
          return this.getResponse(
            url,
            { pageSize: '250', customerName },
            { 'X-Rsc-Page': ExclusiveStartKey, 'X-Loading-Id': loadingId }
          );
        }

        // failing all those chesks, we have nothing to do here.
        return EMPTY;
      }),
      map((response) => response.body),
      tap((generators) => callback(generators)),
      reduce((generators, thisPage) => [...generators, ...thisPage], []),
      catchError(this.handleError)
    );
  }

  private getResponse(
    url: string,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?:
      | HttpHeaders
      | {
          [header: string]: string | string[];
        }
  ): Observable<HttpResponse<Generator[]>> {
    return this.httpClient.get<Generator[]>(url, {
      observe: 'response',
      params,
      headers,
    });
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(error);
    }

    let message = 'Something bad happened; please try again later.';

    if (error.error.message) {
      message = error.error.message;
    }

    // Return an observable with a user-facing error message.
    return throwError(message);
  }
}
